import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import Select from '../../common/select';
import { AddService } from '../../partial/newAddService';
import { TemplateList } from './TemplateList';
import { VehicleSpecificPricing } from '../../partial/VehicleSpecificPricing';

import ServicesAPI, {
  buildCategoryLookup,
  mergeServicesWithPricing,
  extractPriceMatrix,
  extractAreaConfig,
  buildPriceMatrix,
  filterOverlayCandidates,
  createServiceCategory,
  getServiceItemTemplates,
  getInstallerBodyStyles
} from "../../../services/servicesApiV2";
import InventoryAPI, { getUnitsOfMeasure } from "../../../services/inventoryService";
import ScopeService from "../../../services/scopeService";
import { isInstallerAdmin } from "../../../util/permissions";
import { isMarineEnabled } from "../../../util/marineCapability";
import { HTTPService } from "../../../services/httpService";
import ProductItemTemplateModal from "../../partial/ProductItemTemplateModal";
import WorkflowStepTemplateModal from "../../partial/WorkflowStepTemplateModal";
import ServiceCatalogGrid from "./ServiceCatalogGrid";
import ServiceEditView from "./ServiceEditView";
import { loadServiceCatalog } from "./catalogValidation";
import "./serviceCatalog.css";

const API = new HTTPService();

export default class Services extends Component {

  state = {
    // Categories
    categories: [],                    // Array of category objects (installer's)
    allCategories: [],                 // All global categories
    categoriesById: {},                // Lookup by category_id
    selectedCategoryId: null,
    selectedCategoryName: "Category",
    showAddCategory: false,
    newCategoryName: "",

    // Services (master services with merged pricing)
    services: [],                      // Master services with nested pricing (for selected category)
    allInstallerServices: [],          // All master services for this installer
    selectedServiceId: null,
    selectedServiceName: "Service",

    // Selected service details
    currentService: null,              // Full service object
    serviceName: "",
    coverageLevel: null,               // FRONT | REAR | FULL | null
    notes: "",
    dataCollectRequired: "Y",
    allowMultiple: false,
    taxable: true,
    retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
    wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
    durations: { XL: 0, L: 0, M: 0, S: 0 },
    expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },
    areaConfig: null,                  // extracted area config for the selected service, when area-priced

    // Category configs and units (for expected quantity labels)
    categoryConfigs: {},           // { category_name: { default_unit_id, default_unit_code, ... } }
    unitsById: {},                 // { unit_id: { unit_name, unit_code } }

    // Overlays
    currentOverlays: [],               // Services this one overlays
    availableOverlays: [],             // Candidates for overlay selection

    // UI State
    errors: {},
    message: "",
    showModal: false,
    modalAction: "",
    isTemplateMode: false,
    isLoading: false,

    // Redesigned catalog grid
    catalog: [],                       // normalized categories → services (grid data)
    catalogLoading: false,
    servicesView: "grid",              // 'grid' | 'detail' | 'new'
    newServiceAssetType: "auto",       // 'auto' | 'marine' — chosen before the modal opens
    bodyStyles: [],                    // body styles this installer serves, for the size form's "Applies to" chips
    products: [],                      // installer's inventory products, for the area material picker

    // Vehicle-Specific Pricing
    showVehiclePricingModal: false,
    trimOverrideCount: 0,
    vehicleOverrides: [],  // List of vehicles with custom pricing

    // Service Item Templates
    showProductItemTemplateModal: false,
    showWorkflowStepTemplateModal: false,
    productItemCount: 0,
    workflowStepCount: 0
  }

  async componentDidMount() {
    await this.loadInstallerData();
    await this.loadCatalog();

    // Body styles for the size form's "Applies to" restriction. Non-fatal: an
    // empty list renders as "All body styles", which is also what [] means on
    // the wire, so a failed load costs the picker, not the save.
    try {
      const bodyStyles = await getInstallerBodyStyles(this.props.typeId);
      this.setState({ bodyStyles });
    } catch (err) {
      console.error("Failed to load body styles for the service form:", err);
    }

    if (isMarineEnabled()) {
      try {
        const products = await InventoryAPI.getProducts(this.props.typeId);
        this.setState({ products: products || [] });
      } catch (err) {
        console.error("Failed to load products for the area service form:", err);
      }
    }
  }

  // Load the full normalized catalog for the redesigned grid landing
  loadCatalog = async () => {
    try {
      this.setState({ catalogLoading: true });
      const { catalog } = await loadServiceCatalog(this.props.typeId);
      this.setState({ catalog, catalogLoading: false });
    } catch (error) {
      console.error("Error loading service catalog:", error);
      this.setState({ catalogLoading: false });
    }
  };

  // Load installer's services and derive available categories
  loadInstallerData = async () => {
    try {
      this.setState({ isLoading: true });

      // Load all global categories, category configs, and units in parallel
      const [allCategories, categoryConfigsRaw, unitsRaw] = await Promise.all([
        ServicesAPI.getServiceCategories(),
        API.get('/serviceCategoryConfig').then(r => r.data).catch(() => []),
        getUnitsOfMeasure().catch(() => [])
      ]);
      const categoriesById = buildCategoryLookup(allCategories);

      // Build category config lookup by name
      const categoryConfigs = {};
      if (Array.isArray(categoryConfigsRaw)) {
        categoryConfigsRaw.forEach(config => {
          categoryConfigs[config.category_name] = config;
        });
      }

      // Build units lookup by ID
      const unitsById = {};
      if (Array.isArray(unitsRaw)) {
        unitsRaw.forEach(unit => {
          unitsById[unit.unit_id] = unit;
        });
      }

      // Load all master services for this installer
      const installerServices = await ServicesAPI.getMasterServicesByInstaller(this.props.typeId);

      // Extract unique category IDs from installer's services
      const installerCategoryIds = [...new Set(installerServices.map(s => s.category_id))];

      // Filter to only categories this installer has services in
      const installerCategories = allCategories.filter(cat =>
        installerCategoryIds.includes(cat.category_id)
      );

      this.setState({
        categories: installerCategories,
        allCategories,
        categoriesById,
        categoryConfigs,
        unitsById,
        allInstallerServices: installerServices,
        selectedCategoryId: null,
        selectedCategoryName: "Category",
        isLoading: false
      });
    } catch (error) {
      console.error("Error loading installer data:", error);
      this.setState({ isLoading: false });
      alert("Error loading data. Please refresh the page.");
    }
  };

  // Load services for selected category
  loadServices = async (categoryId, forceRefresh = false) => {
    try {
      this.setState({ isLoading: true });

      let masterServices;

      if (forceRefresh || this.state.allInstallerServices.length === 0) {
        // Fetch fresh data from API
        masterServices = await ServicesAPI.getMasterServicesByCategory(
          this.props.typeId,
          categoryId
        );
      } else {
        // Filter from cached data
        masterServices = this.state.allInstallerServices.filter(
          s => s.category_id === categoryId
        );
      }

      // Get all pricing for this installer
      const allPricing = await ServicesAPI.getInstallerServices(this.props.typeId);

      // Merge services with their pricing
      const servicesWithPricing = mergeServicesWithPricing(masterServices, allPricing);

      this.setState({
        services: servicesWithPricing,
        selectedServiceId: null,
        selectedServiceName: "Service",
        currentService: null,
        serviceName: "",
        coverageLevel: null,
        notes: "",
        dataCollectRequired: "Y",
        retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
        wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
        durations: { XL: 0, L: 0, M: 0, S: 0 },
        expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },
        areaConfig: null,
        currentOverlays: [],
        availableOverlays: [],
        isLoading: false
      });
    } catch (error) {
      console.error("Error loading services:", error);
      this.setState({ isLoading: false });
      alert("Error loading services. Please try again.");
    }
  };

  // Load overlays for a selected service
  loadOverlays = async (serviceId) => {
    try {
      const overrides = await ServicesAPI.getServiceOverrides(serviceId);
      return overrides;
    } catch (error) {
      console.error("Error loading overlays:", error);
      return [];
    }
  };

  /**
   * Can this installer create — and therefore filter by — marine services?
   *
   * ONE rule, read by both the catalog grid's Auto/Marine filter and the New
   * Service panel's Asset type dropdown. Two copies of this condition would
   * eventually disagree, and the failure is silent in both directions: a filter
   * with no way to create what it filters for, or an Asset type dropdown on a
   * page whose list cannot separate the two.
   */
  canUseMarine = () => isMarineEnabled();

  /**
   * Asset type is chosen before the category. A marine service then picks its
   * pricing type — Flat, By size or Measured — in the service modal (spec
   * 2026-09-11). Automotive services are always size-priced.
   *
   * An area-priced AUTOMOTIVE service (a box-truck wrap by the square foot) is
   * a real case the data model allows, and `marineService.js` documents it as
   * the intended model. It is deliberately UNREACHABLE from here, because
   * nothing can price one yet: there is no automotive dimension source at all
   * (`vehicleCompleteness.js:25` — no length/width/wheelbase is ever collected
   * for a car, unlike a boat's LOA/beam), and `manual_area_sqft` is declared in
   * `marineCalc.js` but has no capture UI. The estimate path itself now keys
   * area behaviour off `pricing_kind` (Task 9), not `asset_type` — but a
   * correctly-keyed calculator still has no dimension to calculate from.
   * Offering the combination would create a service that could be selected but
   * never priced.
   */
  handleAssetTypeChange = (event) => {
    const assetType = event.target.value === "Marine" ? "marine" : "auto";
    this.setState({
      newServiceAssetType: assetType
    });
  };

  /**
   * The body-style restriction on the selected service. Every master read emits
   * `body_styles` as a real array of strings, already sorted (server answer
   * 2026-09-16, `masterServiceModel.js` attachBodyStyles) — snake_case only, so
   * there is no camelCase spelling to fall back to. Empty = all body styles,
   * which is also what the server reports when its schema probe finds no
   * `master_service_body_styles` table.
   */
  serviceBodyStyles = () => {
    const raw = (this.state.currentService || {}).body_styles;
    return Array.isArray(raw) ? raw : [];
  };

  // Handle category selection
  handleCategoryChange = async (event) => {
    const { value } = event.target;

    if (value === "Category") {
      this.setState({
        selectedCategoryId: null,
        selectedCategoryName: "Category",
        services: [],
        selectedServiceId: null,
        selectedServiceName: "Service"
      });
      return;
    }

    if (value === "Add New Category") {
      this.setState({
        showAddCategory: true,
        newCategoryName: "",
        selectedCategoryId: null,
        selectedCategoryName: "Category",
        services: [],
        selectedServiceId: null,
        selectedServiceName: "Service"
      });
      return;
    }

    // Find category by name to get ID — check allCategories so newly-selected global categories work
    const category = this.state.allCategories.find(c => c.category_name === value)
      || this.state.categories.find(c => c.category_name === value);
    if (category) {
      this.setState({
        selectedCategoryId: category.category_id,
        selectedCategoryName: value
      });
      await this.loadServices(category.category_id);
    }
  };

  handleNewCategoryChange = (e) => {
    this.setState({ newCategoryName: e.target.value });
  };

  handleCancelNewCategory = () => {
    this.setState({ showAddCategory: false, newCategoryName: "" });
  };

  handleCreateCategory = async () => {
    const name = this.state.newCategoryName.trim();
    if (!name) return;

    try {
      this.setState({ isLoading: true });

      // Get installer_category from the installer's company record
      const company = ScopeService.getCompany() || {};
      const installerCategory = company.installer_category || "Paint";

      const result = await createServiceCategory({
        categoryName: name,
        installerCategory: installerCategory
      });

      // Refresh all categories and select the new one
      await this.loadInstallerData();

      // Re-fetch allCategories to include the new one
      const allCategories = await ServicesAPI.getServiceCategories();
      const categoriesById = buildCategoryLookup(allCategories);

      const newCat = allCategories.find(c => c.category_id === result.id)
        || allCategories.find(c => c.category_name === name);

      this.setState({
        allCategories,
        categoriesById,
        showAddCategory: false,
        newCategoryName: "",
        isLoading: false
      });

      if (newCat) {
        this.setState({
          selectedCategoryId: newCat.category_id,
          selectedCategoryName: newCat.category_name
        });
        await this.loadServices(newCat.category_id);
      }
    } catch (error) {
      console.error("Error creating category:", error);
      this.setState({ isLoading: false });
      alert("Error creating category: " + (error.response?.data?.message || error.message));
    }
  };

  // Handle service selection
  handleServiceChange = async (event) => {
    const { value } = event.target;

    if (value === "Add New Service") {
      this.showService("add");
      return;
    }

    if (value === "Service") {
      this.setState({
        selectedServiceId: null,
        selectedServiceName: "Service",
        currentService: null,
        serviceName: "",
        coverageLevel: null,
        notes: "",
        dataCollectRequired: "Y",
        allowMultiple: false,
        taxable: true,
        retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
        wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
        durations: { XL: 0, L: 0, M: 0, S: 0 },
        expectedQuantities: { XL: 0, L: 0, M: 0, S: 0 },
        areaConfig: null,
        currentOverlays: [],
        availableOverlays: [],
        trimOverrideCount: 0,
        vehicleOverrides: [],
        productItemCount: 0,
        workflowStepCount: 0
      });
      return;
    }

    // Find service by name
    const service = this.state.services.find(s => s.service_name === value);
    if (service) {
      // Extract pricing matrix from the pricing records
      const { retailPrices, wholesalePrices, durations, expectedQuantities } = extractPriceMatrix(service.pricing || []);
      const areaConfig = extractAreaConfig(service);

      // Load current overlays
      const currentOverlays = await this.loadOverlays(service.id);

      // Calculate available overlay candidates
      const availableOverlays = filterOverlayCandidates(this.state.services, service);

      // Load trim overrides (count and list)
      const { count: trimOverrideCount, vehicles: vehicleOverrides } = await this.loadTrimOverrides(service.id);

      // Load service item template counts
      const { productItemCount, workflowStepCount } = await this.loadItemTemplateCounts(service.id);

      this.setState({
        selectedServiceId: service.id,
        selectedServiceName: value,
        currentService: service,
        serviceName: service.service_name,
        coverageLevel: service.coverage_level,
        notes: service.notes || "",
        dataCollectRequired: service.data_collect_required || "Y",
        allowMultiple: service.allow_multiple === 1 || service.allow_multiple === true,
        taxable: service.taxable !== 0 && service.taxable !== false,
        retailPrices,
        wholesalePrices,
        durations,
        expectedQuantities,
        areaConfig,
        currentOverlays,
        availableOverlays,
        trimOverrideCount,
        vehicleOverrides,
        productItemCount,
        workflowStepCount
      });
    }
  };

  // Load a service's full detail into edit state (shared by dropdown + grid entry points)
  selectServiceObject = async (service) => {
    const { retailPrices, wholesalePrices, durations, expectedQuantities } = extractPriceMatrix(service.pricing || []);
    const areaConfig = extractAreaConfig(service);
    const currentOverlays = await this.loadOverlays(service.id);
    const availableOverlays = filterOverlayCandidates(this.state.services, service);
    const { count: trimOverrideCount, vehicles: vehicleOverrides } = await this.loadTrimOverrides(service.id);
    const { productItemCount, workflowStepCount } = await this.loadItemTemplateCounts(service.id);

    this.setState({
      selectedServiceId: service.id,
      selectedServiceName: service.service_name,
      currentService: service,
      serviceName: service.service_name,
      coverageLevel: service.coverage_level,
      notes: service.notes || "",
      dataCollectRequired: service.data_collect_required || "Y",
      allowMultiple: service.allow_multiple === 1 || service.allow_multiple === true,
      taxable: service.taxable !== 0 && service.taxable !== false,
      retailPrices,
      wholesalePrices,
      durations,
      expectedQuantities,
      areaConfig,
      currentOverlays,
      availableOverlays,
      trimOverrideCount,
      vehicleOverrides,
      productItemCount,
      workflowStepCount
    });
  };

  // Grid row → open the service detail view (loads its category + full detail)
  openServiceById = async (serviceId) => {
    const master = this.state.allInstallerServices.find(s => s.id === serviceId);
    if (!master) return;

    this.setState({
      selectedCategoryId: master.category_id,
      selectedCategoryName: this.state.categoriesById[master.category_id]?.name || this.state.selectedCategoryName
    });
    await this.loadServices(master.category_id, false); // populates state.services (resets selection)

    const service = this.state.services.find(s => s.id === serviceId);
    if (service) await this.selectServiceObject(service);
    this.setState({ servicesView: "detail" });
  };

  // Return to the catalog grid, clearing the current selection
  goToGrid = () => {
    this.setState({
      servicesView: "grid",
      selectedServiceId: null,
      selectedServiceName: "Service",
      currentService: null
    });
  };

  // Begin the "new service" flow (category picker → Add Service modal)
  startNewService = () => {
    this.setState({
      servicesView: "new",
      selectedCategoryId: null,
      selectedCategoryName: "Category",
      selectedServiceId: null,
      selectedServiceName: "Service",
      currentService: null,
      newServiceAssetType: "auto"
    });
  };

  // Load trim overrides for a service (returns count and list)
  loadTrimOverrides = async (serviceId) => {
    try {
      const { retailOverrides, wholesaleOverrides } =
        await ServicesAPI.getTrimOverridesForService(this.props.typeId, serviceId);

      // Combine overrides by trim, showing both retail and wholesale prices
      const overridesByTrim = {};

      retailOverrides.forEach(o => {
        const key = o.id_car_trim;
        if (!overridesByTrim[key]) {
          overridesByTrim[key] = {
            trimId: o.id_car_trim,
            make: o.make_name || 'Unknown',
            model: o.model_name || 'Unknown',
            trim: o.trim_name || 'Unknown',
            vehicleSize: o.vehicleSize
          };
        }
        overridesByTrim[key].retailPrice = o.override_price;
      });

      wholesaleOverrides.forEach(o => {
        const key = o.id_car_trim;
        if (!overridesByTrim[key]) {
          overridesByTrim[key] = {
            trimId: o.id_car_trim,
            make: o.make_name || 'Unknown',
            model: o.model_name || 'Unknown',
            trim: o.trim_name || 'Unknown',
            vehicleSize: o.vehicleSize
          };
        }
        overridesByTrim[key].wholesalePrice = o.override_price;
      });

      const vehicleOverrides = Object.values(overridesByTrim)
        .sort((a, b) => {
          const makeCompare = (a.make || '').localeCompare(b.make || '');
          if (makeCompare !== 0) return makeCompare;
          return (a.model || '').localeCompare(b.model || '');
        });
      return {
        count: vehicleOverrides.length,
        vehicles: vehicleOverrides
      };
    } catch (error) {
      console.error("Error loading trim overrides:", error);
      return { count: 0, vehicles: [] };
    }
  };

  // Load service item template counts (product items + workflow steps)
  loadItemTemplateCounts = async (serviceId) => {
    try {
      const templates = await getServiceItemTemplates(serviceId);
      // If server hasn't added the type column, items without type that have
      // product fields are material; items without type and no product fields are work steps.
      const materialCount = (templates || []).filter(t =>
        t.type !== 'work'
      ).length;
      const workCount = (templates || []).filter(t =>
        t.type === 'work'
      ).length;
      return { productItemCount: materialCount, workflowStepCount: workCount };
    } catch (error) {
      console.error("Error loading item template counts:", error);
      return { productItemCount: 0, workflowStepCount: 0 };
    }
  };

  // Refresh template counts after modal changes
  handleItemTemplatesChanged = async () => {
    if (this.state.selectedServiceId) {
      const { productItemCount, workflowStepCount } = await this.loadItemTemplateCounts(this.state.selectedServiceId);
      this.setState({ productItemCount, workflowStepCount });
    }
  };

  // Show vehicle pricing modal
  showVehiclePricing = () => {
    this.setState({ showVehiclePricingModal: true });
  };

  // Hide vehicle pricing modal
  hideVehiclePricing = () => {
    this.setState({ showVehiclePricingModal: false });
  };

  // Handle vehicle pricing update
  handleVehiclePricingUpdate = async () => {
    // Reload the trim overrides (count and list)
    if (this.state.selectedServiceId) {
      const { count: trimOverrideCount, vehicles: vehicleOverrides } =
        await this.loadTrimOverrides(this.state.selectedServiceId);
      this.setState({ trimOverrideCount, vehicleOverrides });
    }
  };

  // Add new service
  addService = async (serviceData) => {
    if (serviceData.pricingMode === 'area' || serviceData.pricingMode === 'flat') {
      return this.addSfRowService(serviceData);
    }

    try {
      this.setState({ isLoading: true });

      // 1. Create master service definition.
      //    bodyStyles: [] means all body styles (never restricted for marine —
      //    see newAddService.jsx).
      const masterServiceResponse = await ServicesAPI.createMasterService({
        installerId: this.props.typeId,
        categoryId: this.state.selectedCategoryId,
        serviceName: serviceData.serviceName,
        dataCollectRequired: serviceData.dataCollectRequired || "Y",
        notes: serviceData.notes || null,
        allowMultiple: serviceData.allowMultiple ? 1 : 0,
        taxable: serviceData.taxable ? 1 : 0,
        // Marine By-size: same rows as auto, but the master must say it is a
        // boat service priced per unit so the estimate never routes it to the
        // area calculator. Coverage level has no marine analogue — omitted,
        // not invented (same convention as areaFactor/wastePct), rather than
        // sent as an explicit null.
        ...(serviceData.assetType === 'marine'
          ? { assetType: 'marine', pricingKind: 'unit', unitOfMeasure: 'each' }
          : { coverageLevel: serviceData.coverageLevel || null }),
        bodyStyles: serviceData.bodyStyles || []
      });

      const newMasterServiceId = masterServiceResponse.id;

      // 2. Create all pricing records using bulk endpoint
      const priceMatrix = buildPriceMatrix(
        serviceData.retailPrices,
        serviceData.wholesalePrices,
        serviceData.durations,
        serviceData.expectedQuantities
      );

      await ServicesAPI.bulkCreatePricing(
        this.props.typeId,
        newMasterServiceId,
        priceMatrix
      );

      // 3. Add overlay relationships if any
      if (serviceData.overlays && serviceData.overlays.length > 0) {
        for (const overriddenId of serviceData.overlays) {
          await ServicesAPI.addServiceOverride(newMasterServiceId, overriddenId);
        }
      }

      alert("Service added successfully!");
      this.setState({ showModal: false, isLoading: false });

      // Refresh installer data (in case this is a new category), then reload
      // this category and select the new service.
      await this.reloadAfterServiceWrite(serviceData.serviceName, { refreshInstaller: true });
      this.setState({ servicesView: "detail" });

    } catch (error) {
      console.error("Error adding service:", error);
      this.setState({ isLoading: false });
      alert("Error adding service: " + (error.response?.data?.message || error.message));
    }
  };

  /**
   * Create a Flat or Measured service — one SF price row per catalog.
   * THE ORDER IS LOAD-BEARING.
   *
   *   1. master service  — the server seeds area_formula from the coverage default
   *   2. material task   — optional; created only when a material product was
   *                        chosen, so material tracking is pre-filled for jobs
   *                        using this service
   *   3. two price rows  — SF/RETAIL and SF/WHOLE
   *
   * bulkCreatePricing is NOT usable here: it refuses an AREA master (the
   * server guard keys on pricing_kind — a unit-priced marine master goes
   * through it like a car), and its loop iterates S/M/L/XL so an SF key would
   * be dropped even if it did not.
   */
  addSfRowService = async (serviceData) => {
    try {
      this.setState({ isLoading: true });

      const isArea = serviceData.pricingMode === 'area';
      // 1. Definition. For Measured the formula is CLIENT-AUTHORED (spec D1)
      //    and sent as data; the server validates structure and variables.
      const master = {
        installerId: this.props.typeId,
        categoryId: this.state.selectedCategoryId,
        serviceName: serviceData.serviceName,
        dataCollectRequired: serviceData.dataCollectRequired || "Y",
        notes: serviceData.notes || null,
        allowMultiple: serviceData.allowMultiple ? 1 : 0,
        taxable: serviceData.taxable ? 1 : 0,
        assetType: serviceData.assetType,
        pricingKind: isArea ? 'area' : 'unit',
        unitOfMeasure: isArea ? serviceData.unitOfMeasure : 'each',
      };
      if (isArea) {
        master.areaFormula = serviceData.areaFormula;
        // Omitted, not invented: an unknown factor/waste must leave the
        // server's stored value alone.
        if (serviceData.areaFactor !== undefined) master.areaFactor = serviceData.areaFactor;
        if (serviceData.wastePct !== undefined) master.wastePct = serviceData.wastePct;
      }
      const masterServiceResponse = await ServicesAPI.createMasterService(master);
      const newMasterServiceId = masterServiceResponse.id;

      try {
        // 2. Material task — optional. Only created when a product was chosen;
        //    a material product is no longer required to save the service.
        if (serviceData.materialProduct) {
          await ServicesAPI.createServiceItemTemplate(newMasterServiceId, {
            label: serviceData.serviceName,
            type: 'material',
            recommended_product_id: serviceData.materialProduct.product_id,
            recommended_product_name: serviceData.materialProduct.product_name || null,
            recommended_product_brand: serviceData.materialProduct.brand || null,
            expected_qty: null,
          });
        }

        // 3. One row per catalog. Same duration on both — a mismatch would change
        //    the schedule based on who is paying.
        for (const [catalog, rate] of [
          ['RETAIL', serviceData.retailRate],
          ['WHOLE', serviceData.wholesaleRate],
        ]) {
          await ServicesAPI.createInstallerService({
            installerId: this.props.typeId,
            masterServiceId: newMasterServiceId,
            vehicleSize: 'SF',
            catalog,
            price: rate,
            durationDays: serviceData.durationDays,
          });
        }

        if (serviceData.overlays && serviceData.overlays.length > 0) {
          for (const overriddenId of serviceData.overlays) {
            await ServicesAPI.addServiceOverride(newMasterServiceId, overriddenId);
          }
        }
      } catch (innerError) {
        // Roll back the master service so a failed create leaves nothing behind.
        // Without this, the master survives with blank rates: it shows up in the
        // catalog, every later save 404s against updatePriceByCompositeKey (which
        // is UPDATE-only), and the only way out is Delete Service — with nothing
        // on screen telling the user that.
        try {
          await ServicesAPI.deleteMasterService(newMasterServiceId);
        } catch (rollbackError) {
          console.error("Failed to roll back partially-created area service", newMasterServiceId, rollbackError);
        }
        throw innerError;
      }

      alert("Service added successfully!");
      this.setState({ showModal: false, isLoading: false });

      await this.reloadAfterServiceWrite(serviceData.serviceName, { refreshInstaller: true });
      this.setState({ servicesView: "detail" });
    } catch (error) {
      console.error("Error adding area service:", error);
      this.setState({ isLoading: false });

      // The server returns a structured 409 (code: DUPLICATE_SERVICE_NAME) when
      // the name collides with an active service in this category. Check the
      // code first; fall back to the string match for any other duplicate shape.
      const code = error.response?.data?.code || '';
      const raw = error.response?.data?.message || error.message || '';
      const isDuplicate = code === 'DUPLICATE_SERVICE_NAME' || /ER_DUP_ENTRY|duplicate/i.test(raw);
      if (isDuplicate) {
        alert(
          "A service with this name already exists in this category. " +
          "Choose a different name and try again."
        );
      } else {
        alert("Error adding service: " + (error.response?.data?.message || error.message));
      }
    }
  };

  // Update existing service
  updateService = async (serviceData) => {
    if (serviceData.pricingMode === 'area' || serviceData.pricingMode === 'flat') {
      return this.updateSfRowService(serviceData);
    }

    try {
      this.setState({ isLoading: true });
      const serviceId = this.state.selectedServiceId;

      // 1. Update master service definition. bodyStyles rides only when the
      //    form touched the picker — an absent key leaves the restriction
      //    unchanged on the server; [] would widen it to all body styles.
      await ServicesAPI.updateMasterService(serviceId, {
        serviceName: serviceData.serviceName,
        dataCollectRequired: serviceData.dataCollectRequired || "Y",
        notes: serviceData.notes || null,
        allowMultiple: serviceData.allowMultiple ? 1 : 0,
        taxable: serviceData.taxable ? 1 : 0,
        // Coverage level has no marine analogue — omitted, not invented (same
        // convention as areaFactor/wastePct), rather than sent as an explicit
        // null.
        ...(serviceData.assetType === 'marine' ? {} : { coverageLevel: serviceData.coverageLevel || null }),
        ...(serviceData.bodyStyles !== undefined ? { bodyStyles: serviceData.bodyStyles } : {})
      });

      // 2. Update pricing records
      const sizes = ['S', 'M', 'L', 'XL'];
      const catalogs = ['RETAIL', 'WHOLE'];

      for (const size of sizes) {
        for (const catalog of catalogs) {
          const price = catalog === 'RETAIL'
            ? serviceData.retailPrices[size]
            : serviceData.wholesalePrices[size];

          const durationDays = typeof serviceData.durations === 'number'
            ? serviceData.durations
            : serviceData.durations[size];

          await ServicesAPI.updatePriceByCompositeKey({
            installerId: this.props.typeId,
            masterServiceId: serviceId,
            vehicleSize: size,
            catalog: catalog,
            price: price || 0,
            durationDays: durationDays || 0,
            expectedQuantity: serviceData.expectedQuantities ? (serviceData.expectedQuantities[size] || 0) : 0
          });
        }
      }

      // 3. Update overlay relationships
      await this.updateOverlays(serviceId, serviceData.overlays || []);

      alert("Service updated successfully!");
      this.setState({ showModal: false, isLoading: false });

      // Refresh services and maintain selection
      await this.loadServices(this.state.selectedCategoryId, true);
      await this.selectServiceByName(serviceData.serviceName);
      await this.loadCatalog();

    } catch (error) {
      console.error("Error updating service:", error);
      this.setState({ isLoading: false });
      alert("Error updating service: " + (error.response?.data?.message || error.message));
    }
  };

  /**
   * Update a Flat or Measured service.
   *
   * Pricing type is immutable (spec D2) and is not sent.
   */
  updateSfRowService = async (serviceData) => {
    try {
      this.setState({ isLoading: true });
      const serviceId = this.state.selectedServiceId;

      const isArea = serviceData.pricingMode === 'area';
      const master = {
        serviceName: serviceData.serviceName,
        dataCollectRequired: serviceData.dataCollectRequired || "Y",
        notes: serviceData.notes || null,
        allowMultiple: serviceData.allowMultiple ? 1 : 0,
        taxable: serviceData.taxable ? 1 : 0,
        assetType: serviceData.assetType,
      };
      if (isArea) {
        // Unit and formula are editable (spec D9); they affect future estimates only.
        master.unitOfMeasure = serviceData.unitOfMeasure;
        master.areaFormula = serviceData.areaFormula;
        if (serviceData.areaFactor !== undefined) master.areaFactor = serviceData.areaFactor;
        if (serviceData.wastePct !== undefined) master.wastePct = serviceData.wastePct;
      }
      await ServicesAPI.updateMasterService(serviceId, master);

      try {
        for (const [catalog, rate] of [
          ['RETAIL', serviceData.retailRate],
          ['WHOLE', serviceData.wholesaleRate],
        ]) {
          await ServicesAPI.updatePriceByCompositeKey({
            installerId: this.props.typeId,
            masterServiceId: serviceId,
            vehicleSize: 'SF',
            catalog,
            price: rate,
            durationDays: serviceData.durationDays,
          });
        }
      } catch (priceError) {
        // A 404 here means this service was left half-created (see addSfRowService's
        // rollback) — it has no $/sq ft price rows for updatePriceByCompositeKey to
        // find, and that endpoint is UPDATE-only so it never will. Say so, rather
        // than surfacing the raw "not found" the server sends.
        if (priceError.response?.status === 404) {
          // The server's price write is an UPSERT now, so a missing variant is
          // created rather than 404ing — which is what lets an installer add a
          // wholesale rate to a service that predates the field. A 404 therefore
          // no longer means "this variant is missing"; it means the SERVICE
          // could not be found for this installer.
          //
          // The old message said creation was interrupted and told the installer
          // to delete and recreate. That was wrong twice over: the case it
          // described no longer produces a 404, and the remedy is destructive on
          // a live service.
          throw new Error(
            "This service could not be found for your account, so its $/sq ft rates were not saved. " +
            "Reload the service list and try again."
          );
        }
        throw priceError;
      }

      await this.updateOverlays(serviceId, serviceData.overlays || []);

      alert("Service updated successfully!");
      this.setState({ showModal: false, isLoading: false });
      await this.loadServices(this.state.selectedCategoryId, true);
      await this.selectServiceByName(serviceData.serviceName);
      await this.loadCatalog();
    } catch (error) {
      console.error("Error updating area service:", error);
      this.setState({ isLoading: false });
      alert("Error updating service: " + (error.response?.data?.message || error.message));
    }
  };

  // Update overlay relationships (diff current vs new)
  updateOverlays = async (serviceId, newOverlayIds) => {
    // API returns { master_service_id, overridden_by_master_service_id, overridden_service_name }
    const currentOverlayIds = this.state.currentOverlays.map(o => o.master_service_id);

    // Find overlays to add
    const toAdd = newOverlayIds.filter(id => !currentOverlayIds.includes(id));

    // Find overlays to remove
    const toRemove = currentOverlayIds.filter(id => !newOverlayIds.includes(id));

    // Add new overlays
    for (const overriddenId of toAdd) {
      await ServicesAPI.addServiceOverride(serviceId, overriddenId);
    }

    // Remove old overlays
    for (const overriddenId of toRemove) {
      await ServicesAPI.removeServiceOverride(serviceId, overriddenId);
    }
  };

  // Delete service
  deleteService = async () => {
    const serviceId = this.state.selectedServiceId;
    const serviceName = this.state.selectedServiceName;

    if (!serviceId) {
      alert("No service selected for deletion");
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${serviceName}"? This will remove all associated pricing. This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      this.setState({ isLoading: true });
      await ServicesAPI.deleteMasterService(serviceId);

      alert("Service deleted successfully!");

      // Reset selection
      this.setState({
        selectedServiceId: null,
        selectedServiceName: "Service",
        currentService: null,
        serviceName: "",
        coverageLevel: null,
        notes: "",
        dataCollectRequired: "Y",
        allowMultiple: false,
        taxable: true,
        retailPrices: { XL: 0, L: 0, M: 0, S: 0 },
        wholesalePrices: { XL: 0, L: 0, M: 0, S: 0 },
        durations: { XL: 0, L: 0, M: 0, S: 0 },
        areaConfig: null,
        currentOverlays: [],
        availableOverlays: [],
        isLoading: false
      });

      // Refresh installer data (in case last service in category was deleted)
      await this.loadInstallerData();
      // Reload services for current category
      if (this.state.selectedCategoryId) {
        await this.loadServices(this.state.selectedCategoryId, true);
      }
      await this.loadCatalog();
      this.setState({ servicesView: "grid" });

    } catch (error) {
      console.error("Error deleting service:", error);
      this.setState({ isLoading: false });

      if (error.response?.status === 400 && error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Error deleting service. Please try again.");
      }
    }
  };

  // Helper to select a service by name after refresh
  /**
   * Reload after a create/update and re-select the service by name.
   *
   * `loadInstallerData()` RESETS `selectedCategoryId` to null (see its setState).
   * Both add paths used to call it and then pass `this.state.selectedCategoryId`
   * straight into `loadServices` — by which point it was null, so the reload
   * fetched category `null`, `selectServiceByName` matched nothing, and the
   * subsequent `servicesView: "detail"` rendered a BLANK PAGE, because the
   * detail branch is gated on a service that was never selected. Editing was
   * unaffected only because it does not refresh installer data.
   *
   * Capture the category first, restore it after, and pass it explicitly.
   */
  reloadAfterServiceWrite = async (serviceName, { refreshInstaller = false } = {}) => {
    const categoryId = this.state.selectedCategoryId;
    const categoryName = this.state.selectedCategoryName;

    if (refreshInstaller) {
      await this.loadInstallerData();
      this.setState({ selectedCategoryId: categoryId, selectedCategoryName: categoryName });
    }

    await this.loadServices(categoryId, true);
    await this.selectServiceByName(serviceName);
    await this.loadCatalog();
  };

  selectServiceByName = async (serviceName) => {
    const service = this.state.services.find(s => s.service_name === serviceName);
    if (service) {
      const { retailPrices, wholesalePrices, durations, expectedQuantities } = extractPriceMatrix(service.pricing || []);
      const areaConfig = extractAreaConfig(service);
      const currentOverlays = await this.loadOverlays(service.id);
      const availableOverlays = filterOverlayCandidates(this.state.services, service);

      this.setState({
        selectedServiceId: service.id,
        selectedServiceName: serviceName,
        currentService: service,
        serviceName: service.service_name,
        coverageLevel: service.coverage_level,
        notes: service.notes || "",
        dataCollectRequired: service.data_collect_required || "Y",
        allowMultiple: service.allow_multiple === 1 || service.allow_multiple === true,
        taxable: service.taxable !== 0 && service.taxable !== false,
        retailPrices,
        wholesalePrices,
        durations,
        expectedQuantities,
        areaConfig,
        currentOverlays,
        availableOverlays
      });
    }
  };

  // Get unit label for the currently selected category
  getSelectedCategoryUnitLabel = () => {
    const { selectedCategoryName, categoryConfigs, unitsById } = this.state;
    const config = categoryConfigs[selectedCategoryName];
    if (!config?.default_unit_id) return '';
    const unit = unitsById[config.default_unit_id];
    return unit?.unit_name || unit?.unit_code || '';
  };

  // Show add/update modal
  showService = (mode) => {
    this.setState({ showModal: true, modalAction: mode });
  };

  // Hide modal
  handleCancel = () => {
    this.setState({ showModal: false });
  };

  // Toggle between services and templates mode
  handleToggleMode = () => {
    this.setState(prevState => ({
      isTemplateMode: !prevState.isTemplateMode,
      selectedCategoryId: null,
      selectedCategoryName: "Category",
      selectedServiceId: null,
      selectedServiceName: "Service",
      services: [],
      currentService: null,
      showModal: false
    }));
  };

  render() {
    if (this.state.isTemplateMode) {
      return this.renderTemplateMode();
    }
    return this.renderServicesMode();
  }

  // Services / Templates segmented pill (matches the catalog redesign)
  renderModeToggle() {
    const onServices = !this.state.isTemplateMode;
    return (
      <div className="text-center mb-3">
        <div className="jv-segmented">
          <button
            type="button"
            aria-pressed={onServices}
            className={`jv-segmented__btn ${onServices ? "is-active" : ""}`}
            onClick={() => this.state.isTemplateMode && this.handleToggleMode()}>
            Services
          </button>
          <button
            type="button"
            aria-pressed={!onServices}
            className={`jv-segmented__btn ${!onServices ? "is-active" : ""}`}
            onClick={() => !this.state.isTemplateMode && this.handleToggleMode()}>
            Templates
          </button>
        </div>
      </div>
    );
  }

  renderTemplateMode() {
    return (
      <React.Fragment>
        {this.renderModeToggle()}
        <TemplateList typeId={this.props.typeId} />
      </React.Fragment>
    );
  }

  renderServicesMode() {
    // Role-based edit permission
    const canEdit = isInstallerAdmin(this.props.user);

    // Generate dropdown lists — show all global categories so installer can pick new ones
    const allCategoryNames = (this.state.allCategories || []).map(c => c.category_name);
    const categoryList = ["Category", ...allCategoryNames, ...(canEdit ? ["Add New Category"] : [])];

    // Normalized service for the edit page (null-aware prices, from the catalog)
    const editSvc = this.state.selectedServiceId
      ? this.state.catalog.flatMap(c => c.services).find(s => s.id === this.state.selectedServiceId)
      : null;

    // SAFETY NET. The detail branch renders nothing at all when `editSvc` is
    // null, so any path that reaches "detail" without a resolvable service
    // produces a blank page with no way back — no grid, no breadcrumb, no error.
    // A specific cause of that is fixed in reloadAfterServiceWrite, but the
    // failure mode is the render's, not that caller's: fall back to the grid so
    // no future path can blank the page either.
    const servicesView =
      this.state.servicesView === "detail" && !editSvc && !this.state.catalogLoading
        ? "grid"
        : this.state.servicesView;

    return (
      <React.Fragment>
        {/* Services / Templates toggle */}
        {canEdit && this.renderModeToggle()}

        {/* Catalog grid landing */}
        {servicesView === "grid" && (
          this.state.catalogLoading
            ? <div className="jv-card"><div className="sc-grid-empty">Loading catalog…</div></div>
            : <ServiceCatalogGrid
                catalog={this.state.catalog}
                canEdit={canEdit}
                showAssetFilter={this.canUseMarine()}
                onOpenService={this.openServiceById}
                onAddService={this.startNewService}
              />
        )}

        {/* New service — category gate (base pricing entered in the modal) */}
        {servicesView === "new" && (
          <div className="jv-card p-4">
            <div className="sc-breadcrumb mb-3">
              <span className="sc-back" onClick={this.goToGrid}>
                <i className="fa-sharp fa-regular fa-arrow-left" /> All services
              </span>
              <span className="sc-crumb-divider" />
              <span className="jv-page-title">New Service</span>
            </div>
            {this.canUseMarine() && (
              <div className="form-row">
                <div className="form-group col-md-4">
                  <Select
                    name="assetType"
                    label="Asset type"
                    options={["Auto", "Marine"]}
                    hideEmpty
                    value={this.state.newServiceAssetType === "marine" ? "Marine" : "Auto"}
                    onChange={this.handleAssetTypeChange}
                  />
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group col-md-4">
                {!this.state.showAddCategory ? (
                  <Select
                    name="categories"
                    label="Category"
                    options={categoryList}
                    value={this.state.selectedCategoryName}
                    onChange={this.handleCategoryChange}
                  />
                ) : (
                  <div>
                    <label>New category</label>
                    <div className="d-flex align-items-center">
                      <input
                        type="text"
                        className="form-control"
                        value={this.state.newCategoryName}
                        onChange={this.handleNewCategoryChange}
                        placeholder="Enter new category name"
                        autoFocus
                      />
                      <Button variant="success" size="sm" className="ml-2"
                        onClick={this.handleCreateCategory}
                        disabled={!this.state.newCategoryName.trim() || this.state.isLoading}>
                        {this.state.isLoading ? '...' : 'Add'}
                      </Button>
                      <Button variant="outline-secondary" size="sm" className="ml-1"
                        onClick={this.handleCancelNewCategory}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="jv-btn jv-btn-primary"
              disabled={!this.state.selectedCategoryId}
              onClick={() => this.showService("add")}
            >
              <i className="fa-sharp fa-solid fa-plus" /> Add Service
            </button>
          </div>
        )}

        {/* Service edit page */}
        {servicesView === "detail" && editSvc && (
          <ServiceEditView
            svc={editSvc}
            catalog={this.state.catalog}
            canEdit={canEdit}
            readOnly={editSvc.isCustom}
            unitLabel={this.getSelectedCategoryUnitLabel()}
            notes={this.state.notes}
            allowMultiple={this.state.allowMultiple}
            taxable={this.state.taxable}
            trimOverrideCount={this.state.trimOverrideCount}
            vehicleOverrides={this.state.vehicleOverrides}
            productItemCount={this.state.productItemCount}
            workflowStepCount={this.state.workflowStepCount}
            areaConfig={this.state.areaConfig}
            bodyStyles={this.serviceBodyStyles()}
            onBack={this.goToGrid}
            onOpenService={this.openServiceById}
            onManageVehicle={this.showVehiclePricing}
            onManageProducts={() => this.setState({ showProductItemTemplateModal: true })}
            onManageWorkflow={() => this.setState({ showWorkflowStepTemplateModal: true })}
            onUpdate={() => this.showService("update")}
            onDelete={this.deleteService}
          />
        )}

        {/* Service Modal */}
        <AddService
          show={this.state.showModal}
          onHide={this.handleCancel}
          installer={this.props.typeId}
          access={this.props.access}
          userRole={this.props.user?.role}
          action={this.state.modalAction}
          // Service data
          categoryId={this.state.selectedCategoryId}
          categoryName={this.state.selectedCategoryName}
          serviceName={this.state.modalAction === "update" ? this.state.serviceName : ""}
          coverageLevel={this.state.modalAction === "update" ? this.state.coverageLevel : null}
          notes={this.state.modalAction === "update" ? this.state.notes : ""}
          dataCollectRequired={this.state.modalAction === "update" ? this.state.dataCollectRequired : "Y"}
          allowMultiple={this.state.modalAction === "update" ? this.state.allowMultiple : false}
          taxable={this.state.modalAction === "update" ? this.state.taxable : true}
          retailPrices={this.state.modalAction === "update" ? this.state.retailPrices : { XL: 0, L: 0, M: 0, S: 0 }}
          wholesalePrices={this.state.modalAction === "update" ? this.state.wholesalePrices : { XL: 0, L: 0, M: 0, S: 0 }}
          durations={this.state.modalAction === "update" ? this.state.durations : { XL: 0, L: 0, M: 0, S: 0 }}
          expectedQuantities={this.state.modalAction === "update" ? this.state.expectedQuantities : { XL: 0, L: 0, M: 0, S: 0 }}
          unitLabel={this.getSelectedCategoryUnitLabel()}
          // Area pricing
          pricingType={
            this.state.modalAction === "update"
              ? (this.state.areaConfig?.pricingType || "size")
              : (this.state.newServiceAssetType === "marine" ? "" : "size")
          }
          unitOfMeasure={this.state.modalAction === "update" ? this.state.areaConfig?.unitOfMeasure ?? "" : ""}
          areaFormula={this.state.modalAction === "update" ? this.state.areaConfig?.areaFormula ?? null : null}
          assetType={
            this.state.modalAction === "update"
              ? (this.state.areaConfig?.assetType || "auto")
              : this.state.newServiceAssetType
          }
          products={this.state.products}
          // Body-style restriction (auto size-priced services only)
          bodyStyles={this.state.bodyStyles}
          initialBodyStyles={this.state.modalAction === "update" ? this.serviceBodyStyles() : []}
          retailRate={this.state.modalAction === "update" ? this.state.areaConfig?.retailRate ?? "" : ""}
          wholesaleRate={this.state.modalAction === "update" ? this.state.areaConfig?.wholesaleRate ?? "" : ""}
          areaFactor={this.state.modalAction === "update" ? this.state.areaConfig?.areaFactor ?? "" : ""}
          wastePct={this.state.modalAction === "update" ? this.state.areaConfig?.wastePct ?? "" : ""}
          areaDuration={this.state.modalAction === "update" ? this.state.areaConfig?.durationDays ?? "" : ""}
          // Overlays
          currentOverlays={this.state.modalAction === "update" ? this.state.currentOverlays : []}
          availableOverlays={this.state.availableOverlays}
          // Callbacks
          onAdd={this.addService}
          onUpdate={this.updateService}
        />

        {/* Vehicle-Specific Pricing Modal */}
        <VehicleSpecificPricing
          show={this.state.showVehiclePricingModal}
          onHide={this.hideVehiclePricing}
          installerId={this.props.typeId}
          preSelectedService={this.state.selectedServiceId ? {
            id: this.state.selectedServiceId,
            service_name: this.state.serviceName
          } : null}
          onUpdate={this.handleVehiclePricingUpdate}
        />

        {/* Product Item Template Modal */}
        {this.state.showProductItemTemplateModal && (
          <ProductItemTemplateModal
            show={this.state.showProductItemTemplateModal}
            onHide={() => this.setState({ showProductItemTemplateModal: false })}
            masterServiceId={this.state.selectedServiceId}
            installerId={this.props.typeId}
            categoryName={this.state.selectedCategoryName}
            serviceName={this.state.serviceName}
            onTemplatesChanged={this.handleItemTemplatesChanged}
          />
        )}

        {/* Workflow Step Template Modal */}
        {this.state.showWorkflowStepTemplateModal && (
          <WorkflowStepTemplateModal
            show={this.state.showWorkflowStepTemplateModal}
            onHide={() => this.setState({ showWorkflowStepTemplateModal: false })}
            masterServiceId={this.state.selectedServiceId}
            onTemplatesChanged={this.handleItemTemplatesChanged}
          />
        )}
      </React.Fragment>
    );
  }
}
